import { useState, useMemo, useEffect } from "react";
import api, { formatAxiosError } from "@/lib/api";
import { X, CheckSquare, Square, Lock } from "@phosphor-icons/react";

// Groups + labels mirror Settings > Permissions so users see the same taxonomy.
export const CAP_GROUPS = [
  {
    title: "Projects & Tasks",
    caps: [
      ["create_projects", "Create projects"],
      ["delete_projects", "Delete projects"],
      ["manage_project_members", "Manage project members"],
      ["create_tasks", "Create tasks"],
      ["delete_tasks", "Delete tasks"],
      ["view_project_reports", "View project reports"],
    ],
  },
  {
    title: "People",
    caps: [
      ["create_users", "Create users"],
      ["disable_users", "Disable / enable users"],
      ["delete_users", "Delete users"],
      ["manage_multi_role", "Assign multiple roles to a user"],
      ["assign_hr", "Assign HR to employees"],
    ],
  },
  {
    title: "HR / Leave",
    caps: [
      ["approve_leaves", "Approve leaves"],
      ["manage_leave_balance", "Edit leave balances"],
      ["create_leave_templates", "Create leave templates"],
    ],
  },
  {
    title: "Assets & Announcements",
    caps: [
      ["create_assets", "Create assets"],
      ["manage_assets", "Manage / assign assets"],
      ["create_announcements", "Post announcements"],
    ],
  },
  {
    title: "Reports & System",
    caps: [
      ["view_reports", "View HR reports"],
      ["manage_permissions", "Edit the permissions matrix"],
      ["manage_roles", "Create / edit / delete roles"],
    ],
  },
  {
    title: "Timesheet",
    caps: [
      ["use_timesheet", "Fill and submit own weekly timesheet"],
      ["approve_timesheets", "Approve / reject timesheets + download reports"],
      ["manage_timesheet_config", "Set global timesheet defaults and per-project approver"],
    ],
  },
  {
    title: "Branding",
    caps: [
      ["manage_branding", "Edit login page + brand identity"],
    ],
  },
  {
    title: "Email",
    caps: [
      ["manage_email", "Configure SMTP + notification email templates"],
    ],
  },
  {
    title: "Expenses",
    caps: [
      ["use_expenses", "Submit and view own expense claims"],
      ["approve_expenses", "Approve or reject submitted expenses"],
      ["mark_expenses_paid", "Mark approved expenses as reimbursed (finance)"],
      ["view_all_expenses", "See everyone's expenses + analytics dashboard"],
      ["manage_expenses_config", "Configure currency, categories, approver role"],
    ],
  },
  {
    title: "Onboarding & Offboarding",
    caps: [
      ["use_onboarding", "Fill in own onboarding checklist"],
      ["manage_onboarding_config", "Configure onboarding sections and fields"],
      ["use_offboarding", "Complete own offboarding tasks when applicable"],
      ["manage_offboarding", "Initiate and finalize offboarding for other users"],
      ["view_employee_documents", "See uploaded documents on other users' profiles"],
    ],
  },
  {
    title: "Idle Screen Tracker",
    caps: [
      ["use_idle_tracker", "This role is tracked (idle time + screenshots captured)"],
      ["view_idle_tracker", "View other users' idle time + screenshots"],
      ["manage_idle_tracker_config", "Configure idle tracker settings (thresholds, retention)"],
    ],
  },
];

const PRESET_COLORS = ["#002FA7", "#FF3B30", "#7C3AED", "#0A0A0A", "#F59E0B", "#10B981", "#EC4899", "#6B7280", "#0EA5E9", "#8B5CF6"];

/**
 * Props:
 *   mode: "create" | "edit"
 *   role?: existing role doc (for edit)
 *   allRoles: full list (for creatable_by picker)
 *   onClose(): dismiss
 *   onSaved(role): after successful save
 */
export default function RoleBuilder({ mode, role, allRoles, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const isAdminRole = isEdit && role?.key === "admin";
  const isProtected = isEdit && !!role?.protected;

  const [key, setKey] = useState(role?.key || "");
  const [label, setLabel] = useState(role?.label || "");
  const [description, setDescription] = useState(role?.description || "");
  const [color, setColor] = useState(role?.color || PRESET_COLORS[0]);
  const [rank, setRank] = useState(role?.rank ?? 10);
  const [order, setOrder] = useState(role?.order ?? 100);
  const [flags, setFlags] = useState({
    is_hr_approver: !!role?.flags?.is_hr_approver,
    is_manager_owner: !!role?.flags?.is_manager_owner,
  });
  const [creatableBy, setCreatableBy] = useState(role?.creatable_by || (isEdit ? [] : ["admin"]));
  const [caps, setCaps] = useState(() => {
    const base = {};
    CAP_GROUPS.forEach(g => g.caps.forEach(([k]) => { base[k] = false; }));
    if (role?.capabilities) {
      Object.entries(role.capabilities).forEach(([k, v]) => { base[k] = !!v; });
    }
    // Sensible default for a new role: nothing but view own stuff (all false).
    return base;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isAdminRole) {
      setCaps(c => ({ ...c, manage_permissions: true, manage_roles: true }));
    }
  }, [isAdminRole]);

  // Auto-generate `key` from label while creating (until user edits key manually)
  const [keyDirty, setKeyDirty] = useState(false);
  useEffect(() => {
    if (isEdit || keyDirty) return;
    const gen = (label || "")
      .toLowerCase()
      .replace(/[^a-z0-9_ ]/g, "")
      .trim()
      .replace(/\s+/g, "_")
      .slice(0, 32);
    setKey(gen);
  }, [label, isEdit, keyDirty]);

  const toggleCap = (k) => {
    if (isAdminRole && (k === "manage_permissions" || k === "manage_roles")) return;
    setCaps(c => ({ ...c, [k]: !c[k] }));
  };
  const toggleCreatable = (rk) => {
    setCreatableBy(prev => prev.includes(rk) ? prev.filter(x => x !== rk) : [...prev, rk]);
  };
  const bulkSet = (group, value) => {
    setCaps(c => {
      const n = { ...c };
      group.caps.forEach(([k]) => {
        if (isAdminRole && (k === "manage_permissions" || k === "manage_roles")) return;
        n[k] = value;
      });
      return n;
    });
  };

  const validate = () => {
    if (!label.trim()) return "Label is required.";
    if (!isEdit) {
      if (!key) return "Key is required.";
      if (!/^[a-z][a-z0-9_]{1,31}$/.test(key)) return "Key must start with a-z and contain only a-z, 0-9, or _ (max 32).";
    }
    const r = Number(rank);
    if (isNaN(r) || r < 1 || r > 100) return "Rank must be between 1 and 100.";
    return "";
  };

  const save = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setSaving(true); setError("");
    try {
      if (isEdit) {
        const payload = {
          label: label.trim(),
          description,
          color,
          rank: Number(rank),
          order: Number(order),
          flags,
          capabilities: caps,
          creatable_by: creatableBy,
        };
        const r = await api.patch(`/roles/${role.key}`, payload);
        onSaved && onSaved(r.data);
      } else {
        const payload = {
          key: key.trim().toLowerCase(),
          label: label.trim(),
          description,
          color,
          rank: Number(rank),
          order: Number(order),
          flags,
          capabilities: caps,
          creatable_by: creatableBy,
        };
        const r = await api.post("/roles", payload);
        onSaved && onSaved(r.data);
      }
      onClose && onClose();
    } catch (err) {
      setError(formatAxiosError(err));
    } finally {
      setSaving(false);
    }
  };

  const otherRoles = useMemo(
    () => (allRoles || []).filter(r => !isEdit || r.key !== role?.key),
    [allRoles, isEdit, role]
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-border w-full max-w-3xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="role-builder-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-white z-10">
          <div>
            <div className="eyebrow flex items-center gap-2">
              {isProtected && <Lock size={12} weight="bold" />} Governance / Role
            </div>
            <h2 className="font-display text-xl font-bold">
              {isEdit ? `Edit ${role.label}` : "New role"}
            </h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted" data-testid="role-builder-close">
            <X size={18} weight="bold" />
          </button>
        </div>

        <form onSubmit={save} className="p-6 space-y-6">
          {/* Identity */}
          <section>
            <div className="eyebrow mb-3">Identity</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold block mb-1">Label</label>
                <input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. Designer"
                  className="w-full border border-border px-3 py-2 text-sm"
                  data-testid="role-label-input"
                />
              </div>
              <div>
                <label className="text-xs font-semibold block mb-1">Key (machine name)</label>
                <input
                  value={key}
                  onChange={(e) => { setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "")); setKeyDirty(true); }}
                  placeholder="designer"
                  disabled={isEdit}
                  className="w-full border border-border px-3 py-2 text-sm font-mono-mini disabled:bg-muted disabled:text-muted-foreground"
                  data-testid="role-key-input"
                />
                <div className="text-[10px] font-mono-mini text-muted-foreground mt-1">
                  {isEdit ? "Key cannot be changed after creation." : "a-z, 0-9, underscore. Auto-generated from label."}
                </div>
              </div>
            </div>
            <div className="mt-3">
              <label className="text-xs font-semibold block mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What does this role do?"
                rows={2}
                className="w-full border border-border px-3 py-2 text-sm"
                data-testid="role-desc-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-3 mt-3">
              <div className="col-span-2">
                <label className="text-xs font-semibold block mb-1">Badge color</label>
                <div className="flex items-center gap-2 flex-wrap">
                  {PRESET_COLORS.map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setColor(c)}
                      className={`w-8 h-8 border-2 ${color === c ? "border-[#0A0A0A]" : "border-transparent"}`}
                      style={{ backgroundColor: c }}
                      title={c}
                      data-testid={`role-color-${c}`}
                    />
                  ))}
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="w-10 h-8 border border-border"
                    title="Custom color"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold block mb-1">Preview</label>
                <span
                  className="inline-block text-[11px] px-2 py-1 uppercase font-mono-mini text-white"
                  style={{ backgroundColor: color }}
                >
                  {label || "role"}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-xs font-semibold block mb-1">Rank (1-100)</label>
                <input
                  type="number"
                  min="1" max="100"
                  value={rank}
                  onChange={(e) => setRank(e.target.value)}
                  disabled={isAdminRole}
                  className="w-full border border-border px-3 py-2 text-sm disabled:bg-muted"
                  data-testid="role-rank-input"
                />
                <div className="text-[10px] font-mono-mini text-muted-foreground mt-1">
                  Higher = more privileged. Admin is fixed at 100.
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold block mb-1">List order</label>
                <input
                  type="number"
                  value={order}
                  onChange={(e) => setOrder(e.target.value)}
                  className="w-full border border-border px-3 py-2 text-sm"
                  data-testid="role-order-input"
                />
                <div className="text-[10px] font-mono-mini text-muted-foreground mt-1">
                  Lower numbers appear first in dropdowns and matrix columns.
                </div>
              </div>
            </div>
          </section>

          {/* Flags */}
          <section>
            <div className="eyebrow mb-3">System flags</div>
            <div className="space-y-2">
              <label className="flex items-start gap-3 border border-border px-3 py-3 cursor-pointer hover:bg-muted">
                <input
                  type="checkbox"
                  checked={flags.is_hr_approver}
                  onChange={() => setFlags(f => ({ ...f, is_hr_approver: !f.is_hr_approver }))}
                  className="mt-1"
                  data-testid="flag-is-hr-approver"
                />
                <div>
                  <div className="text-sm font-semibold">HR-tier leave approver</div>
                  <div className="text-xs text-muted-foreground">
                    Users with this role appear in the pool of HR approvers on employee leave requests.
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-3 border border-border px-3 py-3 cursor-pointer hover:bg-muted">
                <input
                  type="checkbox"
                  checked={flags.is_manager_owner}
                  onChange={() => setFlags(f => ({ ...f, is_manager_owner: !f.is_manager_owner }))}
                  className="mt-1"
                  data-testid="flag-is-manager-owner"
                />
                <div>
                  <div className="text-sm font-semibold">Project owner / manager tier</div>
                  <div className="text-xs text-muted-foreground">
                    Users with this role can own projects and approve their direct team&apos;s leave.
                  </div>
                </div>
              </label>
            </div>
          </section>

          {/* Creatable by */}
          <section>
            <div className="eyebrow mb-3">Who can create users with this role?</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {otherRoles.map(r => (
                <label key={r.key} className={`flex items-center gap-2 border border-border px-3 py-2 text-sm cursor-pointer hover:bg-muted ${creatableBy.includes(r.key) ? "bg-muted" : ""}`}>
                  <input
                    type="checkbox"
                    checked={creatableBy.includes(r.key)}
                    onChange={() => toggleCreatable(r.key)}
                    data-testid={`creatable-by-${r.key}`}
                  />
                  <span
                    className="inline-block text-[10px] px-1.5 py-0.5 uppercase font-mono-mini text-white"
                    style={{ backgroundColor: r.color || "#0A0A0A" }}
                  >
                    {r.label}
                  </span>
                </label>
              ))}
            </div>
            <div className="text-[10px] font-mono-mini text-muted-foreground mt-2">
              Users holding any of the checked roles will see this role in the &ldquo;Add member&rdquo; dropdown.
            </div>
          </section>

          {/* Capabilities */}
          <section>
            <div className="eyebrow mb-3">Capabilities</div>
            {CAP_GROUPS.map(group => (
              <div key={group.title} className="mb-4 border border-border">
                <div className="flex items-center justify-between px-3 py-2 bg-muted border-b border-border">
                  <div className="font-semibold text-xs">{group.title}</div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => bulkSet(group, true)} className="text-[10px] font-mono-mini uppercase hover:text-[#002FA7]" data-testid={`grant-all-${group.title.toLowerCase().replace(/[^a-z]/g, "-")}`}>Grant all</button>
                    <span className="text-muted-foreground">·</span>
                    <button type="button" onClick={() => bulkSet(group, false)} className="text-[10px] font-mono-mini uppercase hover:text-[#FF3B30]" data-testid={`clear-all-${group.title.toLowerCase().replace(/[^a-z]/g, "-")}`}>Clear all</button>
                  </div>
                </div>
                <div>
                  {group.caps.map(([k, lbl]) => {
                    const on = !!caps[k];
                    const locked = isAdminRole && (k === "manage_permissions" || k === "manage_roles");
                    return (
                      <button
                        type="button"
                        key={k}
                        onClick={() => toggleCap(k)}
                        disabled={locked}
                        className={`w-full flex items-center justify-between text-left px-3 py-2 border-t border-border hover:bg-muted ${locked ? "opacity-70 cursor-not-allowed" : ""}`}
                        data-testid={`cap-toggle-${k}`}
                      >
                        <div>
                          <div className="text-sm">{lbl}</div>
                          <div className="text-[10px] font-mono-mini text-muted-foreground">{k}</div>
                        </div>
                        {on
                          ? <CheckSquare size={20} weight="fill" className="text-[#002FA7]" />
                          : <Square size={20} weight="bold" className="text-muted-foreground" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </section>

          {error && (
            <div className="text-xs text-[#FF3B30] border border-[#FF3B30] px-3 py-2" data-testid="role-builder-error">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2 sticky bottom-0 bg-white border-t border-border -mx-6 px-6 py-4">
            <button type="button" onClick={onClose} className="flex-1 border border-border px-4 py-2 text-sm hover:bg-muted">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 btn-primary disabled:opacity-50"
              data-testid="role-builder-save"
            >
              {saving ? "Saving…" : (isEdit ? "Save changes" : "Create role")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
